import useApi from "@/hooks/useApi";
import useShopifyConfigs from "@/hooks/useShopifyConfigs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { SingleValue } from "react-select";
import ShopifyPanelRow from "./ShopifyPanelRow";
import CustomSelect from "../CustomSelect";
import { getScopeOptions } from "./ShopifyScopeOptions";
import { Modal, ModalBody } from "@velaro/velaro-component-lib";
import { ChevronDown } from "@/components/ds/icons";
import {
  SectionCard,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  IconButton,
} from "@/components/ds";

export interface ShopifyMap {
  id: number;
  ShopifyField: SingleValue<Option>;
  create: boolean;
  usedForSearching: boolean;
  showWhenSelected: boolean;
  showInSearch: boolean;
  defaultValue: string;
  scopeType: string;
}

interface Option {
  value: any;
  label: string;
  key?: string;
  disabled?: boolean;
}

interface Props {
  teamId: number;
  scopeType: string;
  showSavedMessage: () => void;
  isCreateMode?: boolean;
  selectedConfigId: number;
  isDeleteDisabled: boolean;
}

interface ShopifyListItem {
  value: number;
  text: string;
}

export interface ShopifyList {
  ShopifyField: string;
  listItems: Option[];
}

export default function ShopifyPanel(props: Props) {
  const [mappedFields, setMappedFields] = useState<ShopifyMap[]>([]);
  const [scopeType, setScopeType] = useState<string>(props.scopeType);
  const [, setSelectedConfig] = useState<number | undefined>(undefined);
  const [metadata, setMetadata] = useState<Option[]>([]);
  const [isLoading, setisLoading] = useState(false);
  const [, setShowModal] = useState(false);
  const [, setPassedField] = useState<ShopifyMap>();
  const { items, loading } = useShopifyConfigs(props.teamId);
  const [listFields, setListFields] = useState<ShopifyList[] | null>(null);
  const api = useApi();
  const [expanded, setExpanded] = useState(true);
  const scopeOptions = getScopeOptions(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [scopeTypeToDelete, setScopeTypeToDelete] = useState<string | null>(null);
  const [isDeleteFieldModalOpen, setIsDeleteFieldModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<{ index: number; fieldName: string } | null>(null);

  useEffect(() => {
    if (!loading) {
      setSelectedConfig(items[0]?.id);
    }
  }, [items, loading]);

  useEffect(() => {
    async function fetchMetadata() {
      if (!scopeType) {
        setMetadata([]);
        return;
      }
      setisLoading(true);

      if (props.selectedConfigId == null) return;

      try {
        const resp = await api.messaging.post(
          `Shopify/Integrations/GetShopifyFields/${props.selectedConfigId}`,
          scopeType
        );
        const json = await resp.json();
        const newOptions = Object.values(json).map((key) => ({
          value: key,
          label: key
        })) as Option[];
        setMetadata(newOptions);
      } catch (err) {
        console.error("ShopifyPanel: failed to load fields", err);
      } finally {
        setisLoading(false);
      }
    }

    fetchMetadata();
  }, [api.messaging, scopeType, props.selectedConfigId]);

  const addRow = async () => {
    const data = [...mappedFields];

    const newMap: ShopifyMap = {
      id: -1,
      ShopifyField: {
        value: "",
        label: ""
      },
      create: false,
      usedForSearching: false,
      showWhenSelected: false,
      showInSearch: false,
      defaultValue: "",
      scopeType: scopeType
    };
    data.push(newMap);
    setMappedFields(data);

    setTimeout(async () => {
      try {
        const resp = await api.messaging.get("Shopify/Integrations/Mapping/");
        const lastId = await resp.json();

        setMappedFields((prevFields) =>
          prevFields.map((field) =>
            field.id === -1 ? { ...field, id: lastId } : field
          )
        );
      } catch (error) {
        console.error("Error fetching lastId:", error);
      }
    }, 1000);
  };

  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    saveRecord();
  }, [mappedFields]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteFieldClick = (index: number) => {
    const field = mappedFields[index];
    const fieldName = field.ShopifyField?.label || 'this field';
    setFieldToDelete({ index, fieldName });
    setIsDeleteFieldModalOpen(true);
  };

  const confirmDeleteField = () => {
    if (fieldToDelete === null) return;

    const index = fieldToDelete.index;
    if (mappedFields[index].id !== -1) {
      deleteRecord(mappedFields[index].id);
    }
    const clone = [...mappedFields];
    clone.splice(index, 1);
    setMappedFields(clone);
    setIsDeleteFieldModalOpen(false);
    setFieldToDelete(null);
  };

  const fetchMapping = useCallback(async () => {
    if (props.selectedConfigId == undefined) return;
    try {
      const resp = await api.messaging.get(
        `Shopify/Integrations/Mapping/${scopeType}/${props.selectedConfigId}`
      );

      if (!resp.ok) {
        console.error("Failed to fetch mapping data:", resp.statusText);
        return;
      }

      const json = await resp.json();

      json.forEach((element: { ShopifyField: { value: any; label: any } }) => {
        const value = {
          value: element.ShopifyField,
          label: element.ShopifyField
        };
        element.ShopifyField = value;
      });
      setMappedFields(json);
    } catch (err) {
      console.error("ShopifyPanel: fetchMapping failed", err);
    }
  }, [api.messaging, scopeType, props.selectedConfigId]);

  useEffect(() => {
    if (!scopeType) return;
    fetchMapping();
  }, [fetchMapping, scopeType]);

  function isOption(value: any): value is Option {
    return value && typeof value === "object" && "value" in value;
  }

  async function saveRecord() {
    const newMap: {
      ShopifyField: any;
      id: number;
      create: boolean;
      usedForSearching: boolean;
      showWhenSelected: boolean;
      showInSearch: boolean;
      defaultValue: string | SingleValue<Option>;
      scopeType: string;
    }[] = [];
    mappedFields.forEach((element) => {
      newMap.push({
        ShopifyField: element.ShopifyField?.value,
        id: element.id,
        create: element.create,
        usedForSearching: element.usedForSearching,
        showWhenSelected: element.showWhenSelected,
        showInSearch: element.showInSearch,
        defaultValue:
          element.defaultValue && isOption(element.defaultValue)
            ? element.defaultValue.value
            : element.defaultValue,
        scopeType: element.scopeType
      });
    });

    try {
      await api.messaging.post(
        `Shopify/Integrations/Mapping/${props.selectedConfigId}`,
        {
          Mappings: newMap
        }
      );
    } catch (err) {
      console.error("ShopifyPanel: saveRecord failed", err);
    }
  }

  async function deleteRecord(id: number) {
    try {
      await api.messaging.delete(`Shopify/Integrations/Mapping/${id}`);
    } catch (err) {
      console.error("ShopifyPanel: deleteRecord failed", err);
    }
  }

  const handleDeleteRecordClick = (scope: string) => {
    setScopeTypeToDelete(scope);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteRecord = async () => {
    if (!scopeTypeToDelete) return;

    try {
      await api.messaging.delete(
        `Shopify/Integrations/Mapping/DeleteRecord/${scopeTypeToDelete}`
      );
    } catch (err) {
      console.error("ShopifyPanel: confirmDeleteRecord failed", err);
    }

    const updatedFields = mappedFields.filter(
      (field) => field.scopeType !== scopeTypeToDelete
    );
    setMappedFields(updatedFields);
    setIsDeleteModalOpen(false);
    setScopeTypeToDelete(null);
  };

  const updateMappedField = (index: number, mappedField: ShopifyMap) => {
    const clone = [...mappedFields];
    clone[index] = mappedField;
    setMappedFields(clone);
  };

  function handleModal(passedField: ShopifyMap) {
    setShowModal(true);
    setPassedField(passedField);
  }

  const fetchList = useCallback(
    async (passedFields: ShopifyMap[]) => {
      if (props.selectedConfigId == null) return;

      try {
        const resp = await api.messaging.post(
          `Shopify/Integrations/GetShopifyFields/${props.selectedConfigId}`,
          { scopeType }
        );

        const json = await resp.json();
        const clone = [...(listFields || [])];

        passedFields.forEach((passedField) => {
          if (
            clone.filter(
              (item) => item.ShopifyField === passedField.ShopifyField?.value
            ).length > 0
          ) {
            clone.splice(
              clone.findIndex(
                (item) => item.ShopifyField === passedField.ShopifyField?.value
              ),
              1
            );
          }
          const innerScopeType = passedField.scopeType;

          const scopeTypeData = json[innerScopeType];
          const ShopifyFieldData = scopeTypeData?.[passedField.ShopifyField?.value];

          if (ShopifyFieldData) {
            const listItems = ShopifyFieldData.map((item: ShopifyListItem) => ({
              value: item.value,
              label: item.text.replace(/&nbsp;/gi, "")
            }));

            clone.push({
              ShopifyField: passedField.ShopifyField?.value,
              listItems
            });
          }
        });
        setListFields(clone);
      } catch (err) {
        console.error("ShopifyPanel: fetchList failed", err);
      }
    },
    [api.messaging, listFields, props.selectedConfigId, scopeType]
  );

  const ready = useMemo(() => !loading, [loading]);
  if (!ready) {
    return <></>;
  }

  const iconClassName = `w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`;

  return (
    <SectionCard
      action={
        <IconButton
          aria-label={expanded ? "Collapse panel" : "Expand panel"}
          onClick={() => setExpanded((s) => !s)}
        >
          <ChevronDown className={iconClassName} />
        </IconButton>
      }
    >
      <div className="mb-4" onClick={(e) => e.stopPropagation()}>
        <CustomSelect
          options={scopeOptions}
          value={scopeType}
          width="w-72"
          onChange={setScopeType}
        />
      </div>
      {expanded && (
        <div>
          <table className="p-4 w-full">
            <thead>
              <tr>
                <th className="pb-4 p-2 pl-4 w-80 text-center text-zinc-700">Shopify field</th>
                <th className="pb-4 p-2 w-80 text-center text-zinc-700">Default value</th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">Create</th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">Used for searching</th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">Show when selected</th>
                <th className="pb-4 p-2 w-24 text-center text-zinc-700">Show in search</th>
                <th className="pb-4 p-2 w-24 text-center"></th>
              </tr>
            </thead>
            <tbody>
              {mappedFields.map((mappedField, i) => (
                <ShopifyPanelRow
                  mappedField={mappedField}
                  i={i}
                  key={i}
                  listFields={listFields || []}
                  updateMappedField={updateMappedField}
                  isLoading={isLoading}
                  deleteRow={handleDeleteFieldClick}
                  handleModal={handleModal}
                  metadata={metadata}
                  fetchList={fetchList}
                  saveRecord={saveRecord}
                  showSavedMessage={props.showSavedMessage}
                />
              ))}
            </tbody>
          </table>

          {scopeType && (
            <div className="flex justify-end pt-4 gap-3">
              <SecondaryButton
                onClick={() => handleDeleteRecordClick(scopeType)}
                disabled={props.isDeleteDisabled || mappedFields.length === 0}
              >
                Delete record
              </SecondaryButton>
              <PrimaryButton onClick={addRow}>
                Add field
              </PrimaryButton>
            </div>
          )}
        </div>
      )}

      {isDeleteModalOpen && (
        <Modal show={isDeleteModalOpen}>
          <ModalBody>
            <div className="mb-6">
              <div className="text-zinc-800 text-xl font-medium">Delete scope type</div>
            </div>
            <div className="text-center text-zinc-700 text-sm font-normal">
              Are you sure you want to delete all field mappings for{" "}
              <strong>{scopeTypeToDelete}</strong>? This action cannot be undone and will remove all mapped fields for this scope type.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteModalOpen(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton onClick={confirmDeleteRecord}>
                Delete
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}

      {isDeleteFieldModalOpen && (
        <Modal show={isDeleteFieldModalOpen}>
          <ModalBody>
            <div className="mb-6">
              <div className="text-zinc-800 text-xl font-medium">Delete field mapping</div>
            </div>
            <div className="text-center text-zinc-700 text-sm font-normal">
              Are you sure you want to delete the field mapping for{" "}
              <strong>{fieldToDelete?.fieldName}</strong>? This action cannot be undone.
            </div>
            <div className="pt-6 flex justify-center gap-4">
              <GhostButton onClick={() => setIsDeleteFieldModalOpen(false)}>
                Cancel
              </GhostButton>
              <PrimaryButton onClick={confirmDeleteField}>
                Delete
              </PrimaryButton>
            </div>
          </ModalBody>
        </Modal>
      )}
    </SectionCard>
  );
}
